import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Plus, Pencil, Trash2, Search, X, Loader2, CheckCircle2, AlertCircle, Users, Radio } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = {
  kode_wilayah: "",
  nama_wilayah: "",
  desa: "",
  kecamatan: "",
  kabupaten: "",
  status: "aktif",
};

const STATUS_MAP = {
  aktif: { label: "Aktif", color: "emerald" },
  pengembangan: { label: "Dalam Pengembangan", color: "amber" },
  maintenance: { label: "Maintenance", color: "rose" },
};

const WilayahForm = ({ initial, onClose, onSuccess }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (initial?.id) await axios.put(`${BACKEND_URL}/api/master/wilayah/${initial.id}`, form, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/master/wilayah`, form, { withCredentials: true });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal simpan"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        <div className="p-5 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-purple-400" />{initial?.id ? "Edit Wilayah" : "Tambah Wilayah"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Kode Wilayah</label>
              <input required type="text" value={form.kode_wilayah} onChange={(e) => setField("kode_wilayah", e.target.value.toUpperCase())} placeholder="WIL-BDG-01" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="wilayah-kode-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Status</label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="wilayah-status-select">
                <option value="aktif">Aktif</option>
                <option value="pengembangan">Dalam Pengembangan</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Wilayah</label>
            <input required type="text" value={form.nama_wilayah} onChange={(e) => setField("nama_wilayah", e.target.value)} placeholder="Dago" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="wilayah-nama-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Desa/Kelurahan</label>
            <input required type="text" value={form.desa} onChange={(e) => setField("desa", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="wilayah-desa-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Kecamatan</label>
            <input required type="text" value={form.kecamatan} onChange={(e) => setField("kecamatan", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="wilayah-kecamatan-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Kabupaten/Kota</label>
            <input required type="text" value={form.kabupaten} onChange={(e) => setField("kabupaten", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="wilayah-kabupaten-input" />
          </div>
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-fuchsia-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="wilayah-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {initial?.id ? "Update" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const MasterWilayahPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const res = await axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }); setItems(res.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus wilayah "${nama}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/master/wilayah/${id}`, { withCredentials: true }); load(); }
    catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const filtered = items.filter((w) => !search ||
    w.nama_wilayah?.toLowerCase().includes(search.toLowerCase()) ||
    w.kode_wilayah?.toLowerCase().includes(search.toLowerCase()) ||
    w.kecamatan?.toLowerCase().includes(search.toLowerCase()) ||
    w.kabupaten?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="wilayah-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase">Master Data</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Data Wilayah / Coverage</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola wilayah cakupan layanan ISP (Desa / Kecamatan / Kabupaten)</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-purple-600/30 flex items-center gap-2" data-testid="add-wilayah-btn">
          <Plus className="w-4 h-4" />Tambah Wilayah
        </button>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari kode/nama/kecamatan..." className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>
          <span className="text-xs text-slate-500">Total: <strong className="text-purple-400">{filtered.length}</strong></span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="wilayah-table">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400">
                <th className="py-2.5 px-3">Kode</th>
                <th className="py-2.5 px-3">Nama Wilayah</th>
                <th className="py-2.5 px-3">Desa</th>
                <th className="py-2.5 px-3">Kecamatan</th>
                <th className="py-2.5 px-3">Kabupaten</th>
                <th className="py-2.5 px-3 text-center">ODP</th>
                <th className="py-2.5 px-3 text-center">Pelanggan</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading ? (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="py-6 text-center text-slate-500 italic">Belum ada wilayah</td></tr>
              ) : filtered.map((w) => {
                const s = STATUS_MAP[w.status] || STATUS_MAP.aktif;
                return (
                  <tr key={w.id} className="hover:bg-slate-900/40" data-testid={`wilayah-row-${w.id}`}>
                    <td className="py-2.5 px-3 font-mono font-bold text-purple-300">{w.kode_wilayah}</td>
                    <td className="py-2.5 px-3 font-semibold text-white">{w.nama_wilayah}</td>
                    <td className="py-2.5 px-3 text-slate-300">{w.desa}</td>
                    <td className="py-2.5 px-3 text-slate-300">{w.kecamatan}</td>
                    <td className="py-2.5 px-3 text-slate-300">{w.kabupaten}</td>
                    <td className="py-2.5 px-3 text-center"><span className="px-1.5 py-0.5 rounded bg-slate-800 text-cyan-300 font-mono text-[10px] font-bold flex items-center justify-center gap-1 w-fit mx-auto"><Radio className="w-2.5 h-2.5" />{w.odp_count || 0}</span></td>
                    <td className="py-2.5 px-3 text-center"><span className="px-1.5 py-0.5 rounded bg-slate-800 text-emerald-300 font-mono text-[10px] font-bold flex items-center justify-center gap-1 w-fit mx-auto"><Users className="w-2.5 h-2.5" />{w.pelanggan_count || 0}</span></td>
                    <td className="py-2.5 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold bg-${s.color}-500/20 text-${s.color}-300 border border-${s.color}-500/30`}>{s.label}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setEditing(w); setShowForm(true); }} className="p-1 rounded bg-slate-800 hover:bg-amber-900/60 text-amber-400" data-testid={`edit-wilayah-${w.id}`}><Pencil className="w-3 h-3" /></button>
                        <button onClick={() => handleDelete(w.id, w.nama_wilayah)} className="p-1 rounded bg-slate-800 hover:bg-rose-900/60 text-rose-400" data-testid={`delete-wilayah-${w.id}`}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <WilayahForm initial={editing} onClose={() => setShowForm(false)} onSuccess={load} />}
    </div>
  );
};
